import React, { useEffect, useState } from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import Text from '../text';
import PrimaryButton from '../button/PrimaryButton';
import { DataRow } from 'utils/models/data-row';
import ApiService from 'utils/services/ApiService';
import { Box, Button, Typography } from '@mui/material';
import AlarmTable from '../table/AlarmTable';
import { CircularProgress } from '@mui/material';
import CustomToggle from '../button/switchAlarm';
import theme from 'theme/muiTheme';
import { getTestResults, saveSyncCache, saveTestResults } from 'utils/services/idbService';
import { CustomAlarmSetting } from 'utils/models/location-alarms';
import { all } from 'axios';
import { useDispatch } from 'react-redux';
import { setUpdatedLocations } from 'utils/redux/reducers/updateLocationReducer';

interface AlarmSettingsProps {
	rows: DataRow[];
	siteId: number;
	isOwner?: boolean;
	onClose: () => void;
  isOnline: boolean
}

const AlarmSettings: React.FC<AlarmSettingsProps> = ({
	rows,
	siteId,
	isOwner,
	onClose,
  isOnline
}) => {
	const [locations, setLocations] = useState<any[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [twoWayAlarm, setTwoWayAlarm] = useState(false);
	const [alarmData, setAlarmData] = useState<any[]>([]);
	const dispatch = useDispatch();
	const getLocations = async (noLoading?: boolean) => {
		if (!noLoading) setIsLoading(true);
		try {
			const allSitesData = await getTestResults(
				`allSites`,
				localStorage.getItem('currentUser') ?? 'unknown'
			);
			const defaultTestKits = await getTestResults(`defaultTestKits`, localStorage.getItem('currentUser') ?? 'unknown');
			const SiteIndex = allSitesData?.findIndex((site: any) => site.id === siteId);
			
			const lc = allSitesData[SiteIndex]?.sampleLocationAllVOs.map((location: any) => {
				const customAlarmVOs = location.customAlarmVOs || [];
				
				return {
					...location,
					testKitDescriptors: location.testKitIDs.map((testKitId: number) => {
						const testkit = defaultTestKits.find((dKit: any) => dKit.id === testKitId);
						if (!testkit) return null;
						
						const fieldDescriptors = testkit.fieldDescriptorAllVOs
							.map((fieldDescriptor: any) => {
								if (fieldDescriptor.type === 'Output') {
									// Find matching custom alarm by testKitId and outputId
									const customAlarm = customAlarmVOs.find((alarm: any) => 
									{
										return alarm.testKitId === testkit.id && alarm.outputId === fieldDescriptor.outputId
									}
									);
									
									if (customAlarm) {
										// Use custom alarm values if they exist
										return {
											...fieldDescriptor,
											loLoValue: customAlarm.loLoAlarmValue,
											loValue: customAlarm.loAlarmValue,
											outPutType: fieldDescriptor.outputName,
											hiValue: customAlarm.hiAlarmValue,
											hiHiValue: customAlarm.hiHiAlarmValue,
											outputName: fieldDescriptor.outputName,
										};
									}
									
									// Use default values if no custom alarm exists
									return {
										...fieldDescriptor,
										loLoValue: fieldDescriptor.loLoAlarmValue,
										loValue: fieldDescriptor.loAlarmValue,
										outPutType: fieldDescriptor.outputName,
										hiValue: fieldDescriptor.hiAlarmValue,
										hiHiValue: fieldDescriptor.hiHiAlarmValue,
										outputName: fieldDescriptor.outputName
									};
								}
								return null;
							})
							.filter(Boolean);
							
						return {
							...testkit,
							outPutAlarmsVos: fieldDescriptors
						};
					}).filter(Boolean),
					dataPointAllVOs: []
				};
			});
			
			setLocations(lc || []);
			setIsLoading(false);
			
		} catch (error) {
			console.log(error);
			setIsLoading(false);
		}
	};

	useEffect(() => {
		const fetchData = async () => {
			await getLocations();
		};
		fetchData();
	}, []);

	const handleToggle = (event: {
		target: { checked: boolean | ((prevState: boolean) => boolean) };
	}) => {
		setTwoWayAlarm(event.target.checked);
	};

	const handleAlarmData = (data: any) => {
		setAlarmData(data);
	};

	const headers = [
		{ name: 'Locations', field: 'locations', colSpan: 'col-span-2' },
		{ name: 'Test Kit', field: 'testkit', colSpan: 'col-span-2' },
		{ name: 'Results', field: 'results', colSpan: 'col-span-2' },
		{ name: '', field: 'action', colSpan: 'col-span-2' },
	];
	const alarmValueIsNull = (val: any) => {
		return (val === 0 || val === '' || val === null || val === undefined || isNaN(Number(val)))
	}

	const handleSave = async () => {
		localStorage.setItem(`${siteId}-twoWayAlarm`, JSON.stringify(twoWayAlarm));
		if (isLoading) return;

		setIsLoading(true);
		try {
			for (const alarm of alarmData) {
				const vo = alarm.outPutAlarmsVo;
				if (alarmValueIsNull(vo.hiHiValue))
					vo.hiHiValue = null;
				if (alarmValueIsNull(vo.hiValue))
					vo.hiValue = null;
				if (alarmValueIsNull(vo.loValue))
					vo.loValue = null;
				if (alarmValueIsNull(vo.loLoValue))
					vo.loLoValue = null;
			}

			if (!navigator.onLine) {
				await updateCustomAlarmCache(alarmData as any);
				await getLocations();
				setIsLoading(false);
				return;
			}

			const alarmDiffs = alarmData.map((alarm: any) => {
				const location = locations.find((l) => l.id === alarm.sampleLocationId);
				const descriptor = location?.testKitDescriptors
					?.flatMap((tk: any) => tk.outPutAlarmsVos)
					?.find((o: any) => o.outputName === alarm.dataSetName);
				const changes: string[] = [];
				if (descriptor) {
					if (descriptor.hiHiValue !== alarm.outPutAlarmsVo.hiHiValue) changes.push(`HiHi: ${descriptor.hiHiValue} → ${alarm.outPutAlarmsVo.hiHiValue}`);
					if (descriptor.hiValue   !== alarm.outPutAlarmsVo.hiValue)   changes.push(`Hi: ${descriptor.hiValue} → ${alarm.outPutAlarmsVo.hiValue}`);
					if (descriptor.loValue   !== alarm.outPutAlarmsVo.loValue)   changes.push(`Lo: ${descriptor.loValue} → ${alarm.outPutAlarmsVo.loValue}`);
					if (descriptor.loLoValue !== alarm.outPutAlarmsVo.loLoValue) changes.push(`LoLo: ${descriptor.loLoValue} → ${alarm.outPutAlarmsVo.loLoValue}`);
				}

				return changes.length > 0
					? `${location?.name} - ${alarm.dataSetName}: ${changes.join(', ')}`
					: null;
			}).filter(Boolean);

			const comments = `Updated alarms: ${alarmDiffs.join(' | ')}`;

			const response = await ApiService.updateAlarmSettings(alarmData, siteId, comments);
			await updateCustomAlarmCache(response.data as any);
			await getLocations();
		} catch (error) {
			console.error('Error updating alarm settings:', error);
		} finally {
			setIsLoading(false);
		}
	};

	async function updateCustomAlarmCache(alarmData: any[]) {
		let cachedKits = await getTestResults(
			`defaultTestKits`,
			localStorage.getItem('currentUser') ?? 'unknown'
		);

		const fieldIDtoTestKitID = new Map<number, number>();
		for (let kit of cachedKits) {
			for (let desc of kit.fieldDescriptorAllVOs) {
				fieldIDtoTestKitID.set(desc.id, kit.id);
			}
		}

		const locIDtoAlarmsMap = new Map<number, any[]>();
		for (let alarm of alarmData) {
			let alarmToCache = {
				testKitId: fieldIDtoTestKitID.get(alarm.testkitOutputId),
				outputId: alarm.dataSetName,
				hiHiAlarmValue: alarm.outPutAlarmsVo.hiHiValue,
				hiAlarmValue: alarm.outPutAlarmsVo.hiValue,
				loAlarmValue: alarm.outPutAlarmsVo.loValue,
				loLoAlarmValue: alarm.outPutAlarmsVo.loLoValue,
			} as CustomAlarmSetting;

			if (locIDtoAlarmsMap.has(alarm.sampleLocationId)) {
				const existingAlarms = locIDtoAlarmsMap.get(alarm.sampleLocationId);
				// Find if there's an existing alarm with same testKitId and outputId
				const existingAlarmIndex = existingAlarms?.findIndex(
					existing => 
						existing.testKitId === alarmToCache.testKitId && 
						existing.outputId === alarmToCache.outputId
				);

				if (existingAlarmIndex !== undefined && existingAlarmIndex > -1 && existingAlarms) {
					// Merge the new values with existing values
					const existingAlarm = existingAlarms[existingAlarmIndex];
					alarmToCache = {
						...existingAlarm, // This preserves the existing ID
						...alarmToCache,
						id: existingAlarm.id, // Explicitly preserve the ID
					};
					existingAlarms[existingAlarmIndex] = alarmToCache;
				} else {
					// Only generate new ID for new alarms
					// alarmToCache.id = new Date().getTime();
					existingAlarms?.push(alarmToCache);
				}
			} else {
				// Only generate new ID for new alarms
				// alarmToCache.id = new Date().getTime();
				// alarmToCache.identity = "new"
				locIDtoAlarmsMap.set(alarm.sampleLocationId, [alarmToCache]);
			}
		}
		
		let allData = await getTestResults(
			`allSites`,
			localStorage.getItem('currentUser') ?? 'unknown'
		);

		let site = allData.find((site: {id: number}) => site.id === siteId);
		const updatedLocations = [];

		for (let location of site.sampleLocationAllVOs) {
			if (locIDtoAlarmsMap.has(location.id)) {
				let alarmSet = locIDtoAlarmsMap.get(location.id);
				if (!alarmSet) {
					continue;
				}

				// Initialize customAlarmVOs if it doesn't exist
				if (!location.customAlarmVOs) {
					location.customAlarmVOs = [];
				}

				for (let alarm of alarmSet) {
					let alarmIndex = location.customAlarmVOs.findIndex(
						(customAlarm: {testKitId: number, outputId: string}) => 
							customAlarm.testKitId === alarm.testKitId && 
							customAlarm.outputId === alarm.outputId
					);

					if (alarmIndex > -1) {
						// Merge with existing alarm values
						const existingAlarm = location.customAlarmVOs[alarmIndex];
						location.customAlarmVOs[alarmIndex] = {
							...existingAlarm,
							...alarm,
							id: existingAlarm.id,
						};
					} else {
						location.customAlarmVOs.push(alarm);
					}
				}
				
				// Add the updated location to our array
				updatedLocations.push({
					id: location.id,
					siteFK: siteId,
					name: location.name,
					testKitIDs: location.testKitIDs,
					customAlarmVOs: location.customAlarmVOs,
					action: "UPDATE"
				});
			}
		}

		await saveTestResults(
			`allSites`,
			allData,
			localStorage.getItem('currentUser') ?? 'unknown'
		);

		// If offline, save to locationSyncCache
		if (!navigator.onLine) {
			for (const location of updatedLocations) {
				const locationSync = {
					id: location.id,
					siteFK: siteId,
					name: location.name,
					testKitNames: location.testKitIDs.map((id: any) => cachedKits.find((kit: any) => kit.id === id).testKitName),
					customAlarmVOs: location.customAlarmVOs?.map((alarm: any) => ({
						...(alarm.id ? { id: alarm.id } : {}), // Only include id if it exists
						testKitId: alarm.testKitId,
						outputId: alarm.outputId,
						testKitOutputDescId: cachedKits.find((kit: any) => kit.id === alarm.testKitId).fieldDescriptorAllVOs.find((desc: any) => desc.outputId === alarm.outputId).id,
						unit: cachedKits.find((kit: any) => kit.id === alarm.testKitId).fieldDescriptorAllVOs.find((desc: any) => desc.outputId === alarm.outputId).unit,
						hiHiAlarmValue: alarm.hiHiAlarmValue,
						hiAlarmValue: alarm.hiAlarmValue,
						loAlarmValue: alarm.loAlarmValue,
						loLoAlarmValue: alarm.loLoAlarmValue
					})),
					action: "UPDATE"
				};
				await saveSyncCache(locationSync as any, "locationSyncCache");
			}
		}
	}
	const handleAlarmChange = () => {
		//Saving to a specific view should re-open into that view PWA-2370/2091
		if (localStorage.getItem(`${siteId}-twoWayAlarm`) !== null) {
			setTwoWayAlarm(JSON.parse(localStorage.getItem(`${siteId}-twoWayAlarm`)!));
			return;
		}
		let hasLowAlarms = false;
		
		for (let location of locations) {
			for (let descriptor of location.testKitDescriptors) {
				for (let alarm of descriptor?.outPutAlarmsVos) {
					// Check if any low alarms exist and have non-null, non-zero values
					if ((alarm.loLoValue || alarm.loValue) && 
						(Number(alarm.loLoValue) !== 0 || Number(alarm.loValue) !== 0)) {
						hasLowAlarms = true;
						setTwoWayAlarm(true);
						return; // Exit early once we find any low alarms
					}
				}
			}
		}
		
		// If no low alarms found, set to one-way
		if (!hasLowAlarms) {
			setTwoWayAlarm(false);
		}
	}

	// Call handleAlarmChange whenever locations are loaded or updated
	useEffect(() => {
		handleAlarmChange();
	}, [locations]);

	return (
		<div>
			<div className="flex">
				<Text id="alarm-settings-header" addStyle="!text-[#2C2C2C] !font-inter !text-[20px] !mt-[24px] !font-semibold !mb-[10px]">
					Alarm Settings
				</Text>
				<Box sx={{ flexGrow: 1 }}></Box>
				<FormControlLabel
					className="mt-4"
					label={twoWayAlarm ? 'Two-Way Alarm' : 'One-Way Alarm'}
					labelPlacement="start"
					control={
						<CustomToggle
							checked={twoWayAlarm}
							onChange={handleToggle}
						/>
					}
					sx={{
						marginRight: theme.spacing(1), // Use theme spacing for consistent spacing
						// If you need to adjust the space between the label and the switch, you can modify the marginRight value
						'& .MuiTypography-root': {
							// Adjust the label typography styling if needed
							marginRight: theme.spacing(1), // This adds space between label and switch
						},
					}}
				/>
			</div>

			<Text addStyle="!text-[#2C2C2C] !font-jost !text-[16px] !mt-[24px]">
				You can adjust the alarm settings for each sampling location and test
				you run at this site. <br></br>
				<em>Note: Changes apply to new tests only. To adjust alarms for past tests, change the <br></br>settings here then re-save the individual test you want to update.</em>
			</Text>

			<div className="pt-[16px]">
				{isLoading ? (
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							height: '200px',
						}}
					>
						<CircularProgress />
					</div>
				) : (
					<AlarmTable
						locations={locations}
						twoWayAlarm={twoWayAlarm}
						onPrepareData={handleAlarmData}
						isOwner={isOwner}
					/>
				)}
			</div>
			{isOwner && (
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'flex-end',
						marginTop: 'auto',
						paddingTop: '16px',
					}}
				>
					<Button 
						onClick={onClose}
						color="primary" className="!text-[#9EACB4]">
						CANCEL
					</Button>
					{isLoading ? (
						<CircularProgress
							sx={{ color: '#0092C8' }}
							size={22}
							className="!ml-5"
						/>
					) : (
						<PrimaryButton
							addStyle="!h-[36px] !w-[68px] !ml-5"
							onClick={handleSave}
            //   disabled={!isOnline}
						>
							SAVE
						</PrimaryButton>
					)}
				</Box>
			)}
		</div>
	);
};

export default AlarmSettings;
